import type { LocationBoundary } from '@aloft/toolkit-shared';
import type { FeatureCollection } from 'geojson';
import type { Map } from 'mapbox-gl';
import { RefObject, useEffect, useMemo } from 'react';

import MapBox from '../../map/MapBox.ts';

const SOURCE_ID = 'municipality-boundary-source';
const FILL_LAYER_ID = 'municipality-boundary-fill';
const OUTLINE_LAYER_ID = 'municipality-boundary-outline';
const BOUNDARY_COLOR = '#F97316';

type BoundaryGeoJSON = FeatureCollection;

const EMPTY: BoundaryGeoJSON = { type: 'FeatureCollection', features: [] };

const coordsToGeoJSON = (boundary: LocationBoundary | undefined): BoundaryGeoJSON => {
  if (!boundary || boundary.coords.length < 3) return EMPTY;
  const ring = boundary.coords.map(({ longitude, latitude }) => [longitude, latitude]);
  const first = ring[0];
  const last = ring.at(-1);
  if (first && last && (first[0] !== last[0] || first[1] !== last[1])) {
    ring.push([...first]);
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: { type: boundary.type, name: boundary.name },
        geometry: {
          type: 'Polygon',
          coordinates: [ring],
        },
      },
    ],
  };
};

const ensureBoundaryLayers = (map: Map, geojson: BoundaryGeoJSON) => {
  const source = map.getSource(SOURCE_ID);
  if (source && source.type === 'geojson') {
    source.setData(geojson);
  } else {
    map.addSource(SOURCE_ID, { type: 'geojson', data: geojson });
  }

  if (!map.getLayer(FILL_LAYER_ID)) {
    map.addLayer({
      id: FILL_LAYER_ID,
      type: 'fill',
      source: SOURCE_ID,
      layout: {},
      paint: {
        'fill-color': BOUNDARY_COLOR,
        'fill-opacity': 0.1,
      },
    });
  } else {
    map.setPaintProperty(FILL_LAYER_ID, 'fill-color', BOUNDARY_COLOR);
    map.setPaintProperty(FILL_LAYER_ID, 'fill-opacity', 0.1);
  }

  if (!map.getLayer(OUTLINE_LAYER_ID)) {
    map.addLayer({
      id: OUTLINE_LAYER_ID,
      type: 'line',
      source: SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': BOUNDARY_COLOR,
        'line-width': 2.5,
        'line-dasharray': [2, 2],
      },
    });
  } else {
    map.setPaintProperty(OUTLINE_LAYER_ID, 'line-color', BOUNDARY_COLOR);
  }
};

export function useMunicipalityBoundaryOverlay({
  mapRef,
  mapLoaded,
  enabled,
  locationBoundary,
}: {
  mapRef: RefObject<MapBox | null>;
  mapLoaded: boolean;
  enabled: boolean;
  locationBoundary?: LocationBoundary;
}) {
  const geojson = useMemo(
    () => (enabled ? coordsToGeoJSON(locationBoundary) : EMPTY),
    [enabled, locationBoundary],
  );

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!mapRef.current || !mapLoaded || !map) return;

    const apply = () => ensureBoundaryLayers(map, geojson);
    apply();
    map.on('style.load', apply);
    return () => {
      map.off('style.load', apply);
    };
  }, [geojson, mapLoaded, mapRef]);
}
